import React, { FC, useState, useCallback, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { TableHeaderCell } from './TableHeaderCell'
import { ColumnMenuPopover } from './ColumnMenuPopover'
import { ColumnChooserDrawer } from './ColumnChooserDrawer'
import { TableFooterPagination } from './TableFooterPagination'
import { EnhancedCell } from './components/EnhancedCell'
import { formatCellValue, getCellValue } from './formatters'
import { resolveBundleStatus, resolveBundleStatusMessage } from '../listing-product-table-lite/bundleStatusUtils'
import ProductLinkStatusTab from 'src/app/pages/listing/components/ProductLinkStatusTab'
import LinkButtonGroup from 'src/app/pages/listing/components/LinkButtonGroup'
import { getProductChannelLinkedStatus } from 'src/app/pages/listing/components/linkingStatusUtils'
import { KTIcon } from 'src/marketConnect/helpers'
import Loader from 'src/utils/Loader'
import {
  CatalogColumn,
  SelectedColumn,
  SortState,
  FilterState,
  PaginationState,
  ProductVariant,
  DateRangeFilterValue,
  FilterValue,
} from './types'

interface ProductTableLiteProps {
  // Data
  data: ProductVariant[]
  loading: boolean

  // Columns
  catalogColumns: CatalogColumn[]
  selectedColumns: SelectedColumn[]
  onColumnsChange: (columns: SelectedColumn[]) => void
  onReorderColumns: (dragIndex: number, hoverIndex: number) => void
  onPinColumn?: (field: string, position: 'left' | 'right' | null) => void
  getColumnByField: (field: string) => CatalogColumn | undefined

  // Sorting
  sortState: SortState | null
  onSortChange: (field: string, direction: 'asc' | 'desc') => void

  // Filtering
  filterState: FilterState
  onFilterChange: (filters: FilterState) => void

  // Pagination
  paginationState: PaginationState
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void

  // Actions
  onRefresh?: () => void
  onExportCSV?: () => void
  onResetColumns?: () => void

  // Platform context for special columns
  platformId?: string
  channelId?: string
  mainStore?: boolean
  erpStore?: boolean
  tabsData?: any[]
  onLinkClick?: (rowData: any) => void
  onResyncClick?: (rowData: any) => void
  onResyncSelected?: () => void
  resyncSelectedCount?: number
  resyncSelectedLoading?: boolean
  enableBulkActions?: boolean
  selectedAction?: string
  onSelectedActionChange?: (action: string) => void
  bulkActionOptions?: Array<{ value: string; label: string; requiresSelection?: boolean }>
  selectedCount?: number
  selectionEnabled?: boolean
  selectedRowIds?: Array<number | string>
  onSelectedRowIdsChange?: (ids: Array<number | string>) => void
  isRowSelectable?: (row: ProductVariant) => boolean
  mandatoryColumns?: string[]
  onOpenColumnsConfig?: () => Promise<void> | void
  defaultVisibleFields?: string[]
  //  When set, name/product_name cells link here
  nameLinkBuilder?: (row: ProductVariant) => string | null
  /** Grouped View Lite + OMS: synthetic `grouped_lite_actions` column only. */
  onGroupedLiteEditProduct?: (row: ProductVariant) => void
  /** Flat View + OMS: synthetic `flat_lite_actions` column only for variants. */
  onFlatLiteEditVariant?: (row: ProductVariant) => void
  /** Flat View + OMS: synthetic `flat_lite_actions` column only for variants. */
  onFlatLiteDeleteVariant?: (row: ProductVariant) => void
  /** When false, OMS Edit is disabled (e.g. read-only user). Default true. */
  groupedLiteEditEnabled?: boolean
  /** Grouped View Lite: custom `total_variants` cell + open variants modal on click. */
  onGroupedLiteVariantsClick?: (row: ProductVariant) => void
  /** When true, show synthetic Actions column (Grouped View Lite + OMS). */
  isOmsStore?: boolean
  /** When variants count is 0, show Add Variant button and navigate here. */
  onGroupedLiteAddVariant?: (row: any) => void
  customCellRenderers?: Record<string, (row: ProductVariant, column: CatalogColumn, rowIndex: number) => React.ReactNode>
  /** Fields that use start alignment even when the default table rules would center them (e.g. payout `sync_status`). */
  leftAlignFields?: string[]
  /** Opens the existing image preview/lightbox for image cells. */
  onImagePreview?: (imageUrl: string) => void
  /** OMS only: when true the header select-all checkbox is suppressed (individual row checkboxes still work). */
  hideSelectAllCheckbox?: boolean
  /** OMS only: called when user confirms "Delete Selected". Rendered in toolbar when selectedCount > 0. */
  onDeleteSelected?: () => void
  /** OMS only: loading state for bulk delete button. */
  deleteSelectedLoading?: boolean
}

const LiteAddVariantButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <button
      type='button'
      className='btn btn-sm'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      style={{
        border: `1px dashed ${isHovered ? '#047857' : '#10b981'}`,
        borderRadius: '8px',
        color: isHovered ? '#064e3b' : '#059669',
        backgroundColor: isHovered ? '#d1fae5' : '#ecfdf5',
        padding: '4px 10px',
        fontWeight: '700',
        fontSize: '11px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '1.2',
        whiteSpace: 'pre-line',
        transition: 'all 0.25s ease-in-out',
        minWidth: '94px',
        boxShadow: isHovered ? '0 2px 4px rgba(16, 185, 129, 0.1)' : 'none',
        transform: isHovered ? 'translateY(-1px)' : 'none',
      }}
      title='Add a new variant'
    >
      {`+ Add\nVariant`}
    </button>
  )
}

const TRAILING_SYNTHETIC_FIELDS = new Set(['grouped_lite_actions', 'flat_lite_actions'])

export const ProductTableLite: FC<ProductTableLiteProps> = ({
  data,
  loading,
  catalogColumns,
  selectedColumns,
  onColumnsChange,
  onReorderColumns,
  onPinColumn,
  getColumnByField,
  sortState,
  onSortChange,
  filterState,
  onFilterChange,
  paginationState,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onExportCSV,
  onResetColumns,
  platformId,
  channelId,
  mainStore,
  erpStore,
  tabsData = [],
  onLinkClick,
  onResyncClick,
  onResyncSelected,
  resyncSelectedCount = 0,
  resyncSelectedLoading = false,
  enableBulkActions = false,
  selectedAction = '',
  onSelectedActionChange,
  bulkActionOptions = [
    { value: 'delist', label: 'Delist', requiresSelection: true },
    { value: 'relist', label: 'Relist', requiresSelection: true },
    { value: 'full_update', label: 'Full Update' },
  ],
  selectedCount = 0,
  selectionEnabled = false,
  selectedRowIds = [],
  onSelectedRowIdsChange,
  isRowSelectable,
  mandatoryColumns = ['product_name', 'linking_status', 'link_actions'],
  onOpenColumnsConfig,
  defaultVisibleFields = [],
  nameLinkBuilder,
  onGroupedLiteEditProduct,
  onFlatLiteEditVariant,
  onFlatLiteDeleteVariant,
  groupedLiteEditEnabled = true,
  onGroupedLiteVariantsClick,
  isOmsStore = false,
  onGroupedLiteAddVariant,
  customCellRenderers,
  leftAlignFields,
  onImagePreview,
  hideSelectAllCheckbox = false,
  onDeleteSelected,
  deleteSelectedLoading = false,
}) => {
  const [columnMenuOpen, setColumnMenuOpen] = useState(false)
  const [activeColumn, setActiveColumn] = useState<CatalogColumn | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [columnChooserOpen, setColumnChooserOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(filterState.globalSearch)
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const selectedIdSet = new Set((selectedRowIds || []).map(String))
  const mandatorySet = new Set((mandatoryColumns || []).filter(Boolean))

  const isDateRangeFilterValue = (value: FilterValue | ''): value is DateRangeFilterValue =>
    typeof value === 'object' && value !== null && ('from' in value || 'to' in value)

  const hasFilterValue = (value: FilterValue | '') => {
    if (isDateRangeFilterValue(value)) return Boolean(value.from || value.to)
    return value !== '' && value !== null && value !== undefined
  }

  useEffect(() => {
    setSearchQuery(filterState.globalSearch || '')
  }, [filterState.globalSearch])

  useEffect(() => {
    if (!tableScrollRef.current) return
    tableScrollRef.current.scrollLeft = 0
    tableScrollRef.current.scrollTop = 0
  }, [platformId, channelId])

  // Handle column menu open
  const handleOpenColumnMenu = useCallback(
    (column: CatalogColumn, anchorEl: HTMLElement) => {
      const sameColumn = activeColumn?.field === column.field
      const sameAnchor = menuAnchorEl === anchorEl

      if (columnMenuOpen && sameColumn && sameAnchor) {
        setColumnMenuOpen(false)
        setActiveColumn(null)
        setMenuAnchorEl(null)
        return
      }

      setActiveColumn(column)
      setMenuAnchorEl(anchorEl)
      setColumnMenuOpen(true)
    },
    [activeColumn, menuAnchorEl, columnMenuOpen]
  )

  // Handle column menu close
  const handleCloseColumnMenu = useCallback(() => {
    setColumnMenuOpen(false)
    setActiveColumn(null)
    setMenuAnchorEl(null)
  }, [])

  // Handle sort
  const handleSort = useCallback(
    (direction: 'asc' | 'desc') => {
      if (activeColumn) {
        onSortChange(activeColumn.field, direction)
      }
    },
    [activeColumn, onSortChange]
  )

  // Handle column filter
  const handleColumnFilter = useCallback(
    (value: FilterValue | '', operator: string = 'contains') => {
      if (!activeColumn) return

      const existingFilter = filterState.columnFilters.find(
        (f) => f.field === activeColumn.field
      )
      if (!hasFilterValue(value) && !existingFilter) {
        return
      }

      const newFilters = filterState.columnFilters.filter(
        (f) => f.field !== activeColumn.field
      )

      if (hasFilterValue(value)) {
        newFilters.push({
          field: activeColumn.field,
          operator,
          value,
        })
      }

      onFilterChange({
        ...filterState,
        columnFilters: newFilters,
      })
    },
    [activeColumn, filterState, onFilterChange]
  )

  // Handle global search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value)

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      searchTimeoutRef.current = setTimeout(() => {
        onFilterChange({
          ...filterState,
          globalSearch: value,
        })
      }, 500)
    },
    [filterState, onFilterChange]
  )

  // Handle autosize (placeholder)
  const handleAutosize = useCallback(() => {
    console.log('Autosize column:', activeColumn?.field)
    // TODO: Implement column width calculation
  }, [activeColumn])

  const handleAutosizeAll = useCallback(() => {
    console.log('Autosize all columns')
    // TODO: Implement all columns width calculation
  }, [])

  // Toggle column visibility
  const handleToggleColumn = useCallback(
    (field: string) => {
      const catalogColumn = getColumnByField(field)
      if (mandatorySet.has(field) || catalogColumn?.fixed || catalogColumn?.allowVisibility === 'ALWAYS_VISIBLE') return
      const exists = selectedColumns.find((col) => col.field === field)

      if (exists) {
        // Remove
        const updated = selectedColumns
          .filter((col) => col.field !== field)
          .map((col, idx) => ({ ...col, order: idx }))
        onColumnsChange(updated)
      } else {
        // Add
        const maxOrder = selectedColumns.length > 0
          ? Math.max(...selectedColumns.map((c) => c.order))
          : -1
        onColumnsChange([...selectedColumns, { field, order: maxOrder + 1 }])
      }
    },
    [selectedColumns, onColumnsChange, mandatorySet, getColumnByField]
  )

  // Toggle multiple columns at once (for Select All)
  const handleToggleMultipleColumns = useCallback(
    (fields: string[]) => {
      const allowedFields = fields.filter((field) => {
        if (mandatorySet.has(field)) return false
        const col = getColumnByField(field)
        return !(col?.fixed || col?.allowVisibility === 'ALWAYS_VISIBLE')
      })
      if (allowedFields.length === 0) return
      const existingFields = new Set(selectedColumns.map((col) => col.field))
      const missingFields = allowedFields.filter((field) => !existingFields.has(field))
      if (missingFields.length === 0) return

      let maxOrder = selectedColumns.length > 0
        ? Math.max(...selectedColumns.map((c) => c.order))
        : -1

      const newColumns = missingFields.map((field) => {
        maxOrder += 1
        return { field, order: maxOrder }
      })

      onColumnsChange([...selectedColumns, ...newColumns])
    },
    [selectedColumns, onColumnsChange, mandatorySet, getColumnByField]
  )

  const handleUncheckAllColumns = useCallback(() => {
    const defaultSet = new Set((defaultVisibleFields || []).filter(Boolean))
    const kept = selectedColumns.filter((col) => {
      const catalogColumn = getColumnByField(col.field)
      return (
        defaultSet.has(col.field) ||
        mandatorySet.has(col.field) ||
        Boolean(catalogColumn?.fixed) ||
        catalogColumn?.allowVisibility === 'ALWAYS_VISIBLE'
      )
    })
    const normalized = kept
      .sort((a, b) => a.order - b.order)
      .map((col, idx) => ({ ...col, order: idx }))
    onColumnsChange(normalized)
  }, [selectedColumns, getColumnByField, mandatorySet, onColumnsChange, defaultVisibleFields])

  // Get current filter for active column
  const getCurrentFilter = () => {
    if (!activeColumn) return ''
    const filter = filterState.columnFilters.find(
      (f) => f.field === activeColumn.field
    )
    return filter?.value ?? ''
  }

  const getCurrentOperator = () => {
    if (!activeColumn) return 'contains'
    const filter = filterState.columnFilters.find(
      (f) => f.field === activeColumn.field
    )
    return filter?.operator || activeColumn.filterOperators?.[0] || 'contains'
  }

  const formatHeaderToken = (value: string) => {
    const token = value.trim()
    const lower = token.toLowerCase()

    if (lower === 'sku') return 'SKU'
    if (lower === 'id') return 'ID'
    if (lower === 'api') return 'API'
    if (lower === 'erp') return 'ERP'

    return lower.charAt(0).toUpperCase() + lower.slice(1)
  }

  const formatHeaderLabel = (column: CatalogColumn) => {
    const field = column.field || ''
    if (onGroupedLiteVariantsClick && field === 'total_variants') return 'Variants'
    const apiHeaderName = (column.headerName || '').trim()
    if (apiHeaderName) return apiHeaderName
    if (field === 'product_name' || field === 'name') return 'Product Name'
    if (field === 'linking_status') return 'Linking Status'
    if (field === 'link_actions') return 'Link Actions'
    if (field === 'grouped_lite_actions' || field === 'flat_lite_actions') return 'Actions'
    if (field === 'sku') return 'Channel SKU'
    if (field === 'api_id') return 'SKU Id'
    if (field === 'compare_price') return 'Wholesale Price'
    if (field === 'api_code') return 'Feed Id'
    if (field === 'quantity') return 'Inventory Level'
    if (field === 'api_status') return 'Status'

    if (field.endsWith('_sync_status')) {
      if (field === 'sync_status') return 'Source Sync Status'
      const type = field.replace(/_sync_status$/, '')
      return `${type.split('_').map(formatHeaderToken).join(' ')} Sync Status`
    }

    const rawLabel = (column.label || column.name || field || '').trim()
    if (!rawLabel) return field

    return rawLabel
      .replace(/[_-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(formatHeaderToken)
      .join(' ')
  }

  const isMenuDisabledForColumn = (column: CatalogColumn) => {
    if (!column?.field) return false
    if (column.field === 'grouped_lite_actions' || column.field === 'flat_lite_actions') {
      return true
    }
    return column.sortable === false && column.resizable === false && column.filterable === false
  }

  const hasActiveFilterForField = (field: string) =>
    filterState.columnFilters.some((f) => f.field === field && hasFilterValue(f.value))

  const shouldCenterAlign = (field: string) => {
    if (!field) return false
    return (
      field === 'linking_status' ||
      field === 'link_actions' ||
      field === 'sku' ||
      field === 'quantity' ||
      field.endsWith('_sync_status') ||
      field === 'sync_status' ||
      field === 'total_variants' ||
      field === 'isCreatedByCommercium' ||
      field === 'total_available_quantity' ||
      field === 'total_reserve_quantity' ||
      field === 'grouped_lite_actions' ||
      field === 'flat_lite_actions'
    )
  }

  const isColumnCenterAligned = (field: string) => {
    if (leftAlignFields?.includes(field)) return false
    return shouldCenterAlign(field)
  }

  const shouldWrapCell = (field: string) => {
    if (
      field === 'grouped_lite_actions' || 
      field === 'flat_lite_actions' || 
      field === 'link_actions' ||
      field === 'description'
    ) {
      return false
    }
    return true
  }

  const getRowLinkingStatus = (row: ProductVariant): 'linked' | 'unlinked' => {
    if (!Array.isArray(tabsData) || tabsData.length === 0) {
      return 'unlinked'
    }
    const anyLinked = tabsData.some((item: any) => {
      return getProductChannelLinkedStatus(row, item?.type, item?.id) === 'linked'
    })
    return anyLinked ? 'linked' : 'unlinked'
  }

  const resolveLinkingStatusFilter = (value: FilterValue): 'linked' | 'unlinked' | null => {
    if (value === true || value === 'true') return 'linked'
    if (value === false || value === 'false') return 'unlinked'
    const normalized = String(value).toLowerCase().trim()
    if (normalized === 'linked') return 'linked'
    if (normalized === 'unlinked') return 'unlinked'
    return null
  }

  const renderedRows = React.useMemo(() => data, [data])

  const selectableRows = React.useMemo(
    () => renderedRows.filter((row) => (isRowSelectable ? isRowSelectable(row) : true)),
    [renderedRows, isRowSelectable]
  )

  const isRowSelected = (row: ProductVariant) => selectedIdSet.has(String(row.id))

  const toggleRowSelection = (row: ProductVariant) => {
    if (!onSelectedRowIdsChange) return
    if (isRowSelectable && !isRowSelectable(row)) return
    const id = row.id
    const exists = selectedIdSet.has(String(id))
    if (exists) {
      onSelectedRowIdsChange((selectedRowIds || []).filter((x) => String(x) !== String(id)))
    } else {
      onSelectedRowIdsChange([...(selectedRowIds || []), id])
    }
  }

  const allVisibleSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selectedIdSet.has(String(row.id)))
  const toggleSelectAllVisible = () => {
    if (!onSelectedRowIdsChange) return
    if (allVisibleSelected) {
      const visibleIds = new Set(selectableRows.map((row) => String(row.id)))
      onSelectedRowIdsChange((selectedRowIds || []).filter((id) => !visibleIds.has(String(id))))
    } else {
      const merged = new Set((selectedRowIds || []).map(String))
      selectableRows.forEach((row) => merged.add(String(row.id)))
      onSelectedRowIdsChange(Array.from(merged))
    }
  }

  // Render cell value
  const renderCellValue = (row: ProductVariant, column: CatalogColumn, rowIndex: number) => {
    if (customCellRenderers?.[column.field]) {
      return customCellRenderers[column.field](row, column, rowIndex)
    }

    /** Grouped View Lite: `/api/platform-products-ag-grid` row flag (snake_case). */
    const groupedLiteRowEditable = row?.is_editable === true

    const getLinkedPlatformNames = () => {
      if (!Array.isArray(tabsData) || tabsData.length === 0) return []
      return tabsData
        .filter((item: any) => {
          return getProductChannelLinkedStatus(row, item?.type, item?.id) === 'linked'
        })
        .map((item: any) => item?.name || item?.type)
        .filter(Boolean)
    }

    // Handle Product Name column
    if (column.field === 'product_name' || column.field === 'name') {
      const platform = row?.platform_product?.platform
      const productId = row?.platform_product?.id
      const variantId = row?.id
      const apiName = row?.name || ''
      const productName = row?.platform_product?.name || row?.product_name || ''
      const normalizedApiName = String(apiName).replace(/\s+/g, ' ').trim()
      const normalizedProductName = String(productName).replace(/\s+/g, ' ').trim()
      // All Products search uses row.name. When that is the same title as the product,
      // show row.name so extra spaces match the API instead of a nested field.
      const name =
        apiName &&
        apiName !== 'Default Title' &&
        (!productName || normalizedApiName === normalizedProductName)
          ? apiName
          : productName || apiName

      // When nameLinkBuilder is provided (Grouped View), it is the sole authority:
      //   non-empty string -> clickable link to that URL
      //   empty string / null -> plain text, not clickable
      if (nameLinkBuilder != null) {
        const customNameTo = nameLinkBuilder(row)
        if (customNameTo != null && String(customNameTo).trim() !== '') {
          return (
            <div className='d-flex flex-column'>
              <Link
                className='fw-bold text-info mb-0 text-decoration-none'
                to={customNameTo}
              >
                <span className='text-preserve-spacing'>{name}</span>
              </Link>
            </div>
          )
        }
        return (
          <div className='d-flex flex-column'>
            <span className='fw-bold text-preserve-spacing'>{name}</span>
          </div>
        )
      }

      // Default flat-view: optional variant suffix under the name
      const variantName = row?.name
      const variantSuffix =
        variantName && variantName !== 'Default Title' && variantName !== name
          ? `Variant : ${variantName}`
          : ''

      // OMS flat view: product name uses the exact same navigation as the Edit icon
      if (onFlatLiteEditVariant) {
        const canClick = groupedLiteEditEnabled !== false
        return (
          <div className='d-flex flex-column'>
            <button
              type='button'
              className='fw-bold text-info mb-0 text-decoration-none text-start p-0 border-0 bg-transparent'
              disabled={!canClick}
              onClick={() => {
                if (canClick) onFlatLiteEditVariant(row)
              }}
              style={{ cursor: canClick ? 'pointer' : 'default' }}
              title={canClick ? 'Edit' : undefined}
            >
              <span className='text-preserve-spacing'>{name}</span>
            </button>
            {variantSuffix && <small className='fw-bold text-preserve-spacing'>{variantSuffix}</small>}
          </div>
        )
      }

      // Non-OMS flat view: link to product-variant-detail
      return (
        <div className='d-flex flex-column'>
          <Link
            className='fw-bold text-info mb-0 text-decoration-none'
            to={`/product-variant-detail/${variantId}/platform/${platform}/product/${productId}/${platformId}`}
          >
            <span className='text-preserve-spacing'>{name}</span>
          </Link>
          {variantSuffix && <small className='fw-bold text-preserve-spacing'>{variantSuffix}</small>}
        </div>
      )
    }

    // Handle Linking Status column
    if (column.field === 'linking_status') {
      return (
        <span className='d-inline-flex'>
          <ProductLinkStatusTab data={row} channels={tabsData} />
        </span>
      )
    }

    // Handle Link Actions column
    if (column.field === 'link_actions') {
      return (
        <LinkButtonGroup
          rowData={row}
          rowIndex={rowIndex}
          onClick={() => onLinkClick?.(row)}
          onCloseTooltip={() => { }}
          platformId={platformId}
          mainStore={mainStore}
          erpStore={erpStore}
          channels={tabsData}
        />
      )
    }

    // Grouped View Lite + OMS: synthetic actions column (Edit only when row is editable)
    if (column.field === 'grouped_lite_actions') {
      if (!groupedLiteRowEditable) {
        return <span className='text-muted'>-</span>
      }
      if (!onGroupedLiteEditProduct) {
        return <span className='text-muted'>-</span>
      }
      const canClick = groupedLiteEditEnabled !== false
      return (
        <div className='d-flex justify-content-center align-items-center'>
          <button
            type='button'
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
            disabled={!canClick}
            onClick={() => {
              if (canClick) onGroupedLiteEditProduct(row)
            }}
            title='Edit'
            style={{ cursor: 'pointer' }}
          >
            <KTIcon iconName='pencil' className='fs-4 text-primary' />
          </button>
        </div>
      )
    }

    // Flat View + OMS: synthetic actions column for variants
    if (column.field === 'flat_lite_actions') {
      if (!onFlatLiteEditVariant && !onFlatLiteDeleteVariant) {
        return <span className='text-muted'>-</span>
      }
      const canClick = groupedLiteEditEnabled !== false
      return (
        <div className='d-flex justify-content-center align-items-center'>
          {onFlatLiteEditVariant && (
            <button
              type='button'
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2'
              disabled={!canClick}
              onClick={() => {
                if (canClick) onFlatLiteEditVariant(row)
              }}
              title='Edit Variant'
              style={{ cursor: 'pointer' }}
            >
              <KTIcon iconName='pencil' className='fs-4 text-primary' />
            </button>
          )}
          {onFlatLiteDeleteVariant && (
            <button
              type='button'
              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
              disabled={!canClick}
              onClick={() => {
                if (canClick) onFlatLiteDeleteVariant(row)
              }}
              title='Delete Variant'
              style={{ cursor: 'pointer' }}
            >
              <KTIcon iconName='trash' className='fs-4 text-danger' />
            </button>
          )}
        </div>
      )
    }

    // Grouped View Lite: variant column controlled by total_variants count.
    // is_editable flag intentionally NOT used here (it governs the Edit button only).
    if (column.field === 'total_variants' && onGroupedLiteVariantsClick) {
      const total = Number(row?.total_variants ?? 0)
      const label = `${total} ${total === 1 ? 'Variant' : 'Variants'}`

      // OMS store + no variants yet -> "+ Add Variant" button -> navigates to create variant page.
      if (isOmsStore && total === 0) {
        return (
          <div className='d-flex justify-content-center align-items-center w-100'>
            <LiteAddVariantButton onClick={() => onGroupedLiteAddVariant?.(row)} />
          </div>
        )
      }

      if (!isOmsStore && total === 0) {
        return (
          <div className='d-flex justify-content-center align-items-center w-100 text-muted'>
            NA
          </div>
        )
      }

      // Count pill button opens ProductAllVariantModal. Non-OMS opens read-only modal.
      return (
        <div className='d-flex justify-content-center align-items-center w-100'>
          <button type='button' className='btn btn-sm grouped-lite-variant-pill' onClick={() => onGroupedLiteVariantsClick(row)}>
            {label}
          </button>
        </div>
      )
    }

    // Old grid renders SKU as plain text (not link).
    if (column.field === 'sku') {
      const sku = getCellValue(row, column.field)
      if (!sku) return <span className="text-muted">-</span>
      return <span>{sku}</span>
    }

    const value = getCellValue(row, column.field)

    const isImageColumn =
      String(
        (column as CatalogColumn & {
          advance_option?: { filter_input_type?: string }
        }).advance_option?.filter_input_type || ''
      )
        .trim()
        .toUpperCase() === 'IMAGE'

    if (isImageColumn) {
      const imageUrl = typeof value === 'string' ? value.trim() : ''

      if (!imageUrl) {
        return <span>-</span>
      }

      return (
        <img
          src={imageUrl}
          alt={column.headerName || column.field}
          onClick={(event) => {
            event.stopPropagation()
            onImagePreview?.(imageUrl)
          }}
          style={{
            width: '100px',
            height: '100px',
            objectFit: 'contain',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            cursor: onImagePreview ? 'pointer' : 'default',
            display: 'block',
          }}
        />
      )
    }

    // Determine cell type based on column field
    let cellType: 'text' | 'status' | 'productName' | 'price' | 'badge' | 'apiStatus' | 'date' = 'text'

    if (column.field === 'price' || column.field === 'compare_price' || column.field === 'wholesale_price') {
      cellType = 'price'
    } else if (column.field.includes('sync_status')) {
      cellType = 'status'
    } else if (column.field === 'api_status') {
      cellType = 'apiStatus'
    } else if (column.field.includes('status')) {
      cellType = 'badge'
    } else if (column.field.includes('_at') || column.type === 'date') {
      cellType = 'date'
      // Format date if needed
      return <EnhancedCell value={formatCellValue(value, 'date', column.format)} type={cellType} row={row} />
    }

    // For price fields, we need the raw price object for EnhancedCell to extract currency
    if (cellType === 'price') {
      const rawPrice =
        column.field === 'compare_price' || column.field === 'wholesale_price'
          ? row[column.field] ||
          row?.platform_product?.[column.field] ||
          getCellValue(row, column.field)
          : row.price || row?.platform_product?.price || getCellValue(row, column.field)
      return <EnhancedCell value={rawPrice} type={cellType} row={row} />
    }

    if (cellType === 'status') {
      const syncMessageField =
        column.field === 'sync_status' ? 'sync_message' : column.field.replace(/_status$/, '_message')
      const resolvedStatus = resolveBundleStatus(row, column.field, value)
      const syncMessage = resolveBundleStatusMessage(row, column.field, row?.[syncMessageField])
      const isSyncStatusField = column.field === 'sync_status' || column.field.endsWith('_sync_status')
      const shouldShowResync =
        isSyncStatusField && ['PROCESSING', 'ERROR', 'ERROR_UNLINKED', 'ERROR_NO_MAPPING'].includes(String(resolvedStatus || ''))

      return (
        <div className='d-flex align-items-center justify-content-center gap-2'>
          <EnhancedCell value={resolvedStatus} type={cellType} row={row} syncMessage={syncMessage} />
          {shouldShowResync && (
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id={`resync-${row?.id}`}>Click to resync the product variant</Tooltip>}
            >
              <Button
                variant='link'
                className='p-0 m-0'
                onClick={() => onResyncClick?.(row)}
              >
                <i className='bi bi-arrow-repeat text-danger' />
              </Button>
            </OverlayTrigger>
          )}
        </div>
      )
    }

    return <EnhancedCell value={value} type={cellType} row={row} />
  }

  // Organize columns by pinning
  const organizedColumns = React.useMemo(() => {
    const sorted = [...selectedColumns].sort((a, b) => a.order - b.order)

    const pinnedLeft: SelectedColumn[] = []
    const unpinned: SelectedColumn[] = []
    const pinnedRight: SelectedColumn[] = []
    const trailingSynthetic: SelectedColumn[] = []

    sorted.forEach(col => {
      if (TRAILING_SYNTHETIC_FIELDS.has(col.field)) {
        trailingSynthetic.push({ ...col, pinned: null })
        return
      }

      if (col.pinned === 'left') {
        pinnedLeft.push(col)
      } else if (col.pinned === 'right') {
        pinnedRight.push(col)
      } else {
        unpinned.push(col)
      }
    })

    return { pinnedLeft, unpinned, pinnedRight, all: [...pinnedLeft, ...unpinned, ...pinnedRight, ...trailingSynthetic] }
  }, [selectedColumns])

  // Calculate sticky positions
  const getStickyStyle = (column: SelectedColumn, index: number, isHeader: boolean = false): React.CSSProperties => {
    if (!column.pinned) return {}

    const baseZIndex = isHeader ? 20 : 10

    if (column.pinned === 'left') {
      // Calculate offset from all previous left-pinned columns
      let leftOffset = 0
      for (let i = 0; i < index; i++) {
        const prevCol = organizedColumns.pinnedLeft[i]
        if (prevCol) {
          const catalogCol = getColumnByField(prevCol.field)
          leftOffset += prevCol.width || catalogCol?.width || 150
        }
      }

      return {
        position: 'sticky' as const,
        left: leftOffset,
        zIndex: baseZIndex,
        backgroundColor: '#fff',
        borderRight: index === organizedColumns.pinnedLeft.length - 1 ? '2px solid #e4e6ef' : undefined,
      }
    } else if (column.pinned === 'right') {
      // Calculate offset from all following right-pinned columns
      let rightOffset = 0
      const rightIndex = organizedColumns.pinnedRight.findIndex(c => c.field === column.field)
      for (let i = rightIndex + 1; i < organizedColumns.pinnedRight.length; i++) {
        const nextCol = organizedColumns.pinnedRight[i]
        if (nextCol) {
          const catalogCol = getColumnByField(nextCol.field)
          rightOffset += nextCol.width || catalogCol?.width || 150
        }
      }

      return {
        position: 'sticky' as const,
        right: rightOffset,
        zIndex: baseZIndex,
        backgroundColor: '#fff',
        borderLeft: rightIndex === 0 ? '2px solid #e4e6ef' : undefined,
      }
    }

    return {}
  }

  return (
    <>
      <style>
        {`
          .flat-search-input {
            border: 1px solid #d1d3e0;
            border-radius: 6px;
            padding: 0.5rem 0.75rem 0.5rem 2.35rem;
            font-size: 0.9rem;
            transition: all 0.2s ease;
            background: #ffffff;
          }
          .flat-search-input:focus {
            border-color: #3699ff;
            box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.1);
            outline: none;
          }
          .flat-btn {
            border-radius: 6px;
            padding: 0.45rem 0.9rem;
            font-weight: 600;
            font-size: 0.82rem;
            transition: all 0.2s ease;
            border: none;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            white-space: nowrap;
          }
          .flat-btn-primary {
            background: #3699ff;
            color: #ffffff;
          }
          .flat-btn-primary:hover {
            background: #187de4;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(54, 153, 255, 0.3);
          }
          .flat-btn-secondary {
            background: #f3f6f9;
            color: #7e8299;
          }
          .flat-btn-secondary:hover {
            background: #e4e6ef;
            color: #3f4254;
            transform: translateY(-1px);
          }
          .flat-btn-danger {
            background: #dc2626;
            color: #ffffff;
            border: 1px solid #b91c1c;
            font-weight: 700;
            letter-spacing: 0.01em;
            text-shadow: 0 1px 1px rgba(0, 0, 0, 0.18);
          }
          .flat-btn-danger i {
            color: #ffffff;
            opacity: 1;
          }
          .flat-btn-danger:hover:not(:disabled) {
            background: #991b1b;
            border-color: #7f1d1d;
            color: #ffffff;
            transform: translateY(-1px);
            box-shadow: 0 4px 10px rgba(220, 38, 38, 0.35);
          }
          .flat-btn-danger:focus-visible {
            outline: 2px solid #fecaca;
            outline-offset: 2px;
          }
          .flat-btn-icon {
            border-radius: 8px;
            padding: 0.65rem 0.75rem;
            background: #f3f6f9;
            color: #7e8299;
            border: none;
            transition: all 0.2s ease;
          }
          .flat-btn-icon:hover {
            background: #e4e6ef;
            color: #3f4254;
            transform: translateY(-1px);
          }
          .flat-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none !important;
          }
          .flat-btn.flat-btn-danger:disabled {
            background: #fca5a5;
            border-color: #fca5a5;
            color: #ffffff;
            opacity: 0.9;
            text-shadow: none;
          }
          .bulk-selection-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            border-radius: 9999px;
            padding: 0.35rem 0.85rem;
            font-weight: 700;
            font-size: 0.82rem;
            letter-spacing: 0.01em;
            white-space: nowrap;
            background: #dbeafe;
            color: #1e3a8a;
            border: 1px solid #60a5fa;
          }
          .bulk-selection-pill i {
            color: #2563eb;
            font-size: 0.95rem;
          }
          [data-bs-theme='dark'] .listing-lite-table .bulk-selection-pill {
            background: rgba(37, 99, 235, 0.22);
            color: #dbeafe;
            border-color: #3b82f6;
          }
          [data-bs-theme='dark'] .listing-lite-table .bulk-selection-pill i {
            color: #93c5fd;
          }
          .flat-btn-unselect {
            background: #e4e6ef;
            color: #181c32;
            border: 1px solid #b5b5c3;
            font-weight: 600;
          }
          .flat-btn-unselect i {
            color: #3f4254;
          }
          .flat-btn-unselect:hover:not(:disabled) {
            background: #d1d3e0;
            border-color: #a1a5b7;
            color: #000000;
            transform: translateY(-1px);
          }
          [data-bs-theme='dark'] .listing-lite-table .flat-btn-unselect {
            background: #2b2b40;
            color: #f5f8fa;
            border-color: #565674;
          }
          [data-bs-theme='dark'] .listing-lite-table .flat-btn-unselect i {
            color: #e1e3ea;
          }
          [data-bs-theme='dark'] .listing-lite-table .flat-btn-unselect:hover:not(:disabled) {
            background: #3f3f5a;
            border-color: #7e8299;
            color: #ffffff;
          }
          .listing-lite-table .grouped-lite-edit-btn {
            background: #3699ff;
            color: #ffffff;
            border: 1px solid #3699ff;
            border-radius: 0.5rem;
            padding: 0.25rem 0.75rem;
            font-weight: 400;
            font-size: 0.8125rem;
            line-height: 1.5;
            transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
          }
          .listing-lite-table .grouped-lite-edit-btn:hover:not(:disabled) {
            background: #181c32;
            border-color: #181c32;
            color: #ffffff;
          }
          .listing-lite-table .grouped-lite-edit-btn:focus-visible {
            outline: 2px solid #3699ff;
            outline-offset: 2px;
          }
          .listing-lite-table .grouped-lite-edit-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }
          .listing-lite-table .grouped-lite-variant-pill {
            border-radius: 9999px;
            padding: 0.35rem 0.9rem;
            font-weight: 600;
            font-size: 0.8125rem;
            line-height: 1.35;
            color: #2563eb;
            background: #eff6ff;
            border: 1px solid #93c5fd;
            transition: background-color 0.15s ease, border-color 0.15s ease;
          }
          .listing-lite-table .grouped-lite-variant-pill:hover {
            background: #dbeafe;
            border-color: #60a5fa;
          }
          .listing-lite-table .grouped-lite-variant-pill:focus-visible {
            outline: 2px solid #2563eb;
            outline-offset: 2px;
          }
          .listing-lite-table .grouped-lite-variant-add {
            border-radius: 0.375rem;
            padding: 0.35rem 0.75rem;
            font-weight: 600;
            font-size: 0.8125rem;
            line-height: 1.35;
            color: #15803d;
            background: transparent;
            border: 1px dashed #15803d;
            transition: background-color 0.15s ease;
          }
          .listing-lite-table .grouped-lite-variant-add:hover {
            background: rgba(21, 128, 61, 0.08);
          }
          .listing-lite-table .grouped-lite-variant-add:focus-visible {
            outline: 2px solid #15803d;
            outline-offset: 2px;
          }
          .listing-lite-table .table.table-row-bordered > thead > tr > th,
          .listing-lite-table .table.table-row-bordered > tbody > tr > td {
            border-bottom-color: rgba(0, 0, 0, 0.12) !important;
          }
          .listing-lite-table .table-scroll-container {
            max-height: 62vh;
            overflow: auto;
            position: relative;
          }
          .listing-lite-table .table-scroll-container thead th {
            position: sticky;
            top: 0;
            z-index: 12;
            background: #fff;
          }
        `}
      </style>
      <div className="card listing-lite-table">
        {/* Toolbar */}
        <div className="card-header border-0 py-3 px-4" >
          <div className="card-title">
            {/* Search */}
            <div className="d-flex align-items-center position-relative">
              <i className="bi bi-search position-absolute ms-3 text-muted" style={{ fontSize: '1rem' }}></i>
              <input
                type="text"
                className="flat-search-input"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{ width: '200px' }}
              />
            </div>
          </div>

          <div className="card-toolbar">
            <div className="d-flex flex-md-row flex-column justify-content-md-end align-items-md-center gap-2">
              {/* OMS bulk-delete controls — visible only when rows are selected */}
              {onDeleteSelected && selectedCount > 0 && (
                <>
                  {/* Selected count pill */}
                  <span className="bulk-selection-pill">
                    <i className="bi bi-check2-square"></i>
                    {selectedCount} selected
                  </span>

                  {/* Unselect all */}
                  <button
                    type="button"
                    className="flat-btn flat-btn-unselect"
                    onClick={() => onSelectedRowIdsChange?.([])}
                    title="Clear selection"
                  >
                    <i className="bi bi-x-circle"></i>
                    Unselect All
                  </button>

                  {/* Delete selected */}
                  <button
                    type="button"
                    className="flat-btn flat-btn-danger"
                    onClick={onDeleteSelected}
                    disabled={deleteSelectedLoading}
                    title={`Delete ${selectedCount} selected variant${selectedCount === 1 ? '' : 's'}`}
                  >
                    {deleteSelectedLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-trash3"></i>
                        {`Delete Selected (${selectedCount})`}
                      </>
                    )}
                  </button>
                </>
              )}

              {enableBulkActions && (
                <select
                  className='form-select me-md-1'
                  aria-label='Select Action'
                  value={selectedAction}
                  onChange={(ev) => onSelectedActionChange?.(ev.target.value)}
                  style={{ minWidth: '145px', fontSize: '0.85rem' }}
                >
                  <option value={''}>Action</option>
                  {bulkActionOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={Boolean(option.requiresSelection && !selectedCount)}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              )}

              {/* Column chooser button */}
              <button
                className="flat-btn flat-btn-secondary"
                onClick={async () => {
                  if (onOpenColumnsConfig) {
                    await onOpenColumnsConfig()
                  }
                  setColumnChooserOpen(true)
                }}
              >
                <i className="bi bi-columns-gap"></i>
                Columns
              </button>

              {/* Export CSV button */}
              {onExportCSV && (
                <button
                  className="flat-btn flat-btn-secondary"
                  onClick={onExportCSV}
                  disabled={loading}
                >
                  <i className="bi bi-download"></i>
                  Export CSV
                </button>
              )}

              {/* Reset columns button */}
              {onResetColumns && (
                <button
                  className="flat-btn flat-btn-secondary"
                  onClick={onResetColumns}
                  disabled={loading}
                  title="Reset to default columns"
                >
                  <i className="bi bi-arrow-repeat"></i>
                  Reset
                </button>
              )}

              {onResyncSelected && resyncSelectedCount > 0 && (
                <button
                  className="flat-btn flat-btn-primary"
                  onClick={onResyncSelected}
                  disabled={loading || resyncSelectedLoading}
                  title="Resync selected failed products"
                >
                  <i className="bi bi-upload text-white"></i>
                  {resyncSelectedLoading ? 'Resyncing...' : `Resync Selected (${resyncSelectedCount})`}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card-body p-0">
          <div
            ref={tableScrollRef}
            className="table-responsive table-scroll-container"
            style={{ overflowX: 'auto', position: 'relative' }}
          >
            <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3" style={{ position: 'relative' }}>
              <thead>
                <tr className="fw-bold text-muted">
                  {selectionEnabled && (
                    <th
                      className='text-center'
                      style={{ minWidth: 54, width: 54, padding: '12px 8px', verticalAlign: 'middle' }}
                    >
                      {!hideSelectAllCheckbox && (
                        <input
                          type='checkbox'
                          checked={allVisibleSelected}
                          disabled={selectableRows.length === 0}
                          onChange={toggleSelectAllVisible}
                        />
                      )}
                    </th>
                  )}
                  {organizedColumns.all.map((selectedCol, idx) => {
                    const column = getColumnByField(selectedCol.field)
                    if (!column) return null

                    const pinIndex = selectedCol.pinned === 'left'
                      ? organizedColumns.pinnedLeft.findIndex(c => c.field === selectedCol.field)
                      : selectedCol.pinned === 'right'
                        ? organizedColumns.pinnedRight.findIndex(c => c.field === selectedCol.field)
                        : -1

                    const stickyStyle = getStickyStyle(selectedCol, pinIndex, true)

                    return (
                      <th
                        key={column.field}
                        className={isColumnCenterAligned(column.field) ? 'text-center' : 'text-start'}
                        style={{
                          ...stickyStyle,
                          minWidth: selectedCol.width || column.width || 150,
                          padding: '12px 16px',
                          verticalAlign: 'middle',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <div className='d-flex flex-column gap-2'>
                          <TableHeaderCell
                            column={column}
                            displayLabel={formatHeaderLabel(column)}
                            sortState={sortState}
                            onOpenMenu={handleOpenColumnMenu}
                            menuDisabled={isMenuDisabledForColumn(column)}
                            hasActiveFilter={hasActiveFilterForField(column.field)}
                            align={isColumnCenterAligned(column.field) ? 'center' : 'start'}
                          />
                        </div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={selectedColumns.length + (selectionEnabled ? 1 : 0)} className="text-center py-10">
                      <Loader variant="table" />
                    </td>
                  </tr>
                ) : renderedRows.length === 0 ? (
                  <tr>
                    <td colSpan={selectedColumns.length + (selectionEnabled ? 1 : 0)} className="text-center py-10 text-muted">
                      No data available
                    </td>
                  </tr>
                ) : (
                  renderedRows.map((row, rowIndex) => (
                    <tr key={row.id || rowIndex}>
                      {selectionEnabled && (
                        <td className='text-center' style={{ minWidth: 54, width: 54, padding: '12px 8px' }}>
                          <input
                            type='checkbox'
                            checked={isRowSelected(row)}
                            disabled={Boolean(isRowSelectable && !isRowSelectable(row))}
                            onChange={() => toggleRowSelection(row)}
                          />
                        </td>
                      )}
                      {organizedColumns.all.map((selectedCol, idx) => {
                        const column = getColumnByField(selectedCol.field)
                        if (!column) return null

                        const pinIndex = selectedCol.pinned === 'left'
                          ? organizedColumns.pinnedLeft.findIndex(c => c.field === selectedCol.field)
                          : selectedCol.pinned === 'right'
                            ? organizedColumns.pinnedRight.findIndex(c => c.field === selectedCol.field)
                            : -1

                        const stickyStyle = getStickyStyle(selectedCol, pinIndex, false)

                        return (
                          <td
                            key={column.field}
                            className={`text-dark fw-normal ${isColumnCenterAligned(column.field) ? 'text-center' : ''}`}
                            style={{
                              whiteSpace: shouldWrapCell(column.field) ? 'normal' : 'nowrap',
                              wordBreak: shouldWrapCell(column.field) ? 'break-word' : 'normal',
                              maxWidth: shouldWrapCell(column.field) ? 420 : 240,
                              overflow: 'hidden',
                              textOverflow: shouldWrapCell(column.field) ? 'clip' : 'ellipsis',
                              minWidth: selectedCol.width || column.width || 150,
                              padding: '12px 16px',
                              verticalAlign: 'middle',
                              ...stickyStyle
                            }}
                          >
                            {renderCellValue(row, column, rowIndex)}
                          </td>
                        )
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer pagination */}
        <TableFooterPagination
          paginationState={paginationState}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          loading={loading}
        />

        {/* Column menu popover */}
        {activeColumn && (
          <ColumnMenuPopover
            column={activeColumn}
            isOpen={columnMenuOpen}
            onClose={handleCloseColumnMenu}
            onSortAsc={() => handleSort('asc')}
            onSortDesc={() => handleSort('desc')}
            onAutosize={handleAutosize}
            onAutosizeAll={handleAutosizeAll}
            onFilterChange={handleColumnFilter}
            currentFilter={getCurrentFilter()}
            currentOperator={getCurrentOperator()}
            anchorEl={menuAnchorEl}
          />
        )}

        {/* Column chooser drawer */}
        <ColumnChooserDrawer
          isOpen={columnChooserOpen}
          onClose={() => setColumnChooserOpen(false)}
          catalogColumns={catalogColumns}
          selectedColumns={selectedColumns}
          onToggleColumn={handleToggleColumn}
          onToggleMultipleColumns={handleToggleMultipleColumns}
          onUncheckAllColumns={handleUncheckAllColumns}
          onReorderColumns={onReorderColumns}
          onPinColumn={onPinColumn}
          getColumnByField={getColumnByField}
          mandatoryColumns={mandatoryColumns}
        />
      </div>
    </>
  )
} 
